import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBoxOpen,
  FaClipboardList,
  FaEnvelopeOpenText,
  FaLayerGroup,
  FaPlus,
  FaQuoteLeft,
} from "react-icons/fa";
import { adminApi, catalogApi, ordersApi } from "../../api/services";
import { useAuth } from "../../context/AuthContext";
import "./DashboardHome.css";

const ROLE_RANK = { customer: 1, sales: 2, admin: 3 };

const DashboardHome = () => {
  const { user } = useAuth();
  const role = user?.role || "customer";
  const isSales = (ROLE_RANK[role] || 0) >= ROLE_RANK.sales;
  const isAdmin = role === "admin";

  const [stats, setStats] = useState({
    products: null,
    categories: null,
    orders: null,
    quotes: null,
    enquiries: null,
    myOrders: null,
  });

  useEffect(() => {
    let cancelled = false;
    const set = (patch) =>
      !cancelled && setStats((prev) => ({ ...prev, ...patch }));

    ordersApi
      .mine()
      .then((res) => set({ myOrders: res.data.data.orders?.length ?? 0 }))
      .catch(() => set({ myOrders: 0 }));

    if (isAdmin) {
      catalogApi
        .adminProductStats()
        .then((res) => set({ products: res.data.data.total ?? 0 }))
        .catch(() => set({ products: 0 }));
      catalogApi
        .adminCategories()
        .then((res) => set({ categories: res.data.data.categories?.length ?? 0 }))
        .catch(() => set({ categories: 0 }));
    }

    if (isSales) {
      adminApi
        .orders()
        .then((res) => set({ orders: res.data.data.orders?.length ?? 0 }))
        .catch(() => set({ orders: 0 }));
      adminApi
        .quotes()
        .then((res) => set({ quotes: res.data.data.quotes?.length ?? 0 }))
        .catch(() => set({ quotes: 0 }));
      adminApi
        .enquiries()
        .then((res) => set({ enquiries: res.data.data.enquiries?.length ?? 0 }))
        .catch(() => set({ enquiries: 0 }));
    }

    return () => {
      cancelled = true;
    };
  }, [isAdmin, isSales]);

  const cards = [
    isAdmin && {
      label: "Products",
      value: stats.products,
      icon: FaBoxOpen,
      to: "/dashboard/products",
    },
    isAdmin && {
      label: "Categories",
      value: stats.categories,
      icon: FaLayerGroup,
      to: "/dashboard/categories",
    },
    isSales && {
      label: "Orders",
      value: stats.orders,
      icon: FaClipboardList,
      to: "/dashboard/manage/orders",
    },
    isSales && {
      label: "Quote requests",
      value: stats.quotes,
      icon: FaQuoteLeft,
      to: "/dashboard/manage/quotes",
    },
    isSales && {
      label: "Enquiries",
      value: stats.enquiries,
      icon: FaEnvelopeOpenText,
      to: "/dashboard/manage/enquiries",
    },
    !isSales && {
      label: "My orders",
      value: stats.myOrders,
      icon: FaClipboardList,
      to: "/dashboard/orders",
    },
  ].filter(Boolean);

  return (
    <div className="dash-home">
      <header className="dash-page-head">
        <div>
          <h1>Welcome back, {user?.name?.split(" ")[0] || "there"}</h1>
          <p>
            {isAdmin
              ? "Manage your catalog, orders, and customers from one place."
              : isSales
                ? "Track orders, quotes, and customer enquiries."
                : "Review your orders and saved items."}
          </p>
        </div>
        {isAdmin && (
          <Link to="/dashboard/products/new" className="dash-primary-btn">
            <FaPlus /> Add product
          </Link>
        )}
      </header>

      <div className="dash-stat-grid">
        {cards.map(({ label, value, icon: Icon, to }) => (
          <Link key={label} to={to} className="dash-stat-card">
            <span className="dash-stat-icon">
              <Icon />
            </span>
            <span className="dash-stat-value">
              {value === null ? "—" : value}
            </span>
            <span className="dash-stat-label">{label}</span>
          </Link>
        ))}
      </div>

      <section className="dash-quick">
        <h2>Quick actions</h2>
        <div className="dash-quick-links">
          {isAdmin && (
            <>
              <Link to="/dashboard/products/new">Add a new product</Link>
              <Link to="/dashboard/categories">Manage categories</Link>
              <Link to="/dashboard/users">Manage users</Link>
            </>
          )}
          {isSales && (
            <>
              <Link to="/dashboard/manage/orders">Update order statuses</Link>
              <Link to="/dashboard/manage/quotes">Respond to quotes</Link>
            </>
          )}
          <Link to="/dashboard/orders">View my orders</Link>
          <Link to="/dashboard/track">Track an order</Link>
        </div>
      </section>
    </div>
  );
};

export default DashboardHome;
